import { apiClient } from "@/services/apiClient";
import type { PausableRequestChannel } from "@/services/platformRequestPauseService";

/** Mirrors CertificateCheckoutTransferVerification on the backend. */
export type BankTransferVerification =
  | "pending"
  | "verified"
  | "mismatch"
  | "needs_review"
  | "unverified";

/** Mirrors CertificateCheckoutStatus on the backend. */
export type BankTransferCheckoutStatus = "pending" | "paid" | "failed" | "cancelled";

/** What the model transcribed off the comprobante. Every field may be null. */
export interface BankTransferExtraction {
  amount: string | null;
  currency: string | null;
  destinationAccount: string | null;
  destinationName: string | null;
  originBank: string | null;
  reference: string | null;
  operationNumber: string | null;
  transferDate: string | null;
  confidence: number;
}

/** Our own per-field verdict. The model never decides; this does. */
export interface BankTransferMatchResult {
  verification: BankTransferVerification;
  reasons: string[];
  amountMatched: boolean;
  accountMatch: "exact" | "suffix" | "none";
  nameMatched: boolean;
  referenceMatched: boolean;
  referenceReplayed: boolean;
}

export interface BankTransferReviewDetail {
  extraction?: BankTransferExtraction;
  result?: BankTransferMatchResult;
  /** Present when the check never ran (no gateway key, unreadable blob, thrown). */
  error?: string;
  manualApproval?: { by: string; at: string; note: string | null };
}

export interface BankTransferReviewItem {
  checkoutId: string;
  userId: string;
  amountMinor: number;
  currency: string;
  reference: string | null;
  receiptUrl: string | null;
  submittedAt: string | null;
  verification: BankTransferVerification | null;
  /**
   * Paid, but no request was ever created. The consumer has handed over money
   * and received nothing — the worst state this flow reaches, and why these
   * sort to the top of the queue.
   */
  paidWithoutRequest: boolean;
  /**
   * The checkout's own status. `paid` means the money side is settled — by the
   * automatic match or by an admin approval — which is the precondition the
   * backend enforces before a transfer may be reconciled.
   */
  status: BankTransferCheckoutStatus;
  /**
   * The request this transfer paid for, when one exists. Carries the channel
   * because pausing routes to a different service per channel, and `paused` so
   * the queue shows stopped work without a second fetch.
   */
  request: { id: string; channel: PausableRequestChannel; paused: boolean } | null;
  detail: BankTransferReviewDetail | null;
}

/** Transfers a human still has to look at. The server orders them by urgency. */
export function listBankTransfersForReview(): Promise<BankTransferReviewItem[]> {
  return apiClient
    .get<BankTransferReviewItem[]>("/platform/bank-transfers")
    .then((res) => res.data);
}

/**
 * Confirm the money arrived. Marks the checkout paid and releases the held
 * request through the same event the automatic path uses.
 *
 * The note is stored beside the extraction, so a later question about why this
 * was approved has an answer that is not just "an admin clicked it".
 */
export function approveBankTransfer(checkoutId: string, note?: string): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(`/platform/bank-transfers/${checkoutId}/approve`, { note })
    .then((res) => res.data);
}

/**
 * Retire a transfer whose checkout was abandoned.
 *
 * The third answer, for a cancelled or failed checkout where neither of the
 * other two is true: approving would manufacture a request out of a payment
 * the consumer walked away from, reconciling would claim money arrived that
 * was never settled. The note is required — it is the whole record of why this
 * row left the queue without a payment decision.
 */
export function dismissBankTransfer(checkoutId: string, note: string): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(`/platform/bank-transfers/${checkoutId}/dismiss`, { note })
    .then((res) => res.data);
}

/**
 * Record that a human saw the funds on the bank statement.
 *
 * Deliberately separate from approve: approve says "treat this as paid" for a
 * receipt the automatic pass could not settle; reconcile says "I have seen the
 * money". An auto-verified transfer needs the second without ever needing the
 * first, and the backend refuses to reconcile a checkout that is not yet paid.
 * Reconciling is what takes the row off this queue.
 */
export function reconcileBankTransfer(checkoutId: string, note?: string): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(`/platform/bank-transfers/${checkoutId}/reconcile`, { note })
    .then((res) => res.data);
}
